/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  FeedStatus,
  FeedToken,
  GetAdminV1StoresStoreIdFeedsPathParameters,
  GetAdminV1StoresStoreIdSeoAudits200,
  GetAdminV1StoresStoreIdSeoAuditsPathParameters,
  GetAdminV1StoresStoreIdSeoIssues200,
  GetAdminV1StoresStoreIdSeoIssuesParams,
  GetAdminV1StoresStoreIdSeoIssuesPathParameters,
  IndexNowKey,
  PostAdminV1StoresStoreIdFeedsChannelRotateTokenPathParameters,
  PostAdminV1StoresStoreIdSeoAuditPathParameters,
  PostAdminV1StoresStoreIdSeoIndexnowKeyPathParameters,
  PostAdminV1StoresStoreIdSeoIssuesIssueIdResolvePathParameters,
  SeoAuditQueued,
  SeoIssue
} from '../model';

import { customInstance } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Queued for the worker; the nightly run does the same. Read the result from /seo/audits and /seo/issues
 * @summary Run the SEO audit for the store now
 */
export const postAdminV1StoresStoreIdSeoAudit = (
    { storeId }: PostAdminV1StoresStoreIdSeoAuditPathParameters,
 options?: SecondParameter<typeof customInstance<SeoAuditQueued>>,) => {
      return customInstance<SeoAuditQueued>(
      {url: `/admin/v1/stores/${storeId}/seo/audit`, method: 'POST'
    },
      options);
    }
  /**
 * @summary The last 20 audit runs with counts by severity
 */
export const getAdminV1StoresStoreIdSeoAudits = (
    { storeId }: GetAdminV1StoresStoreIdSeoAuditsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdSeoAudits200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdSeoAudits200>(
      {url: `/admin/v1/stores/${storeId}/seo/audits`, method: 'GET'
    },
      options);
    }
  /**
 * Codes: title and description limits, duplicates across the store, media without alt text, thin descriptions, invalid JSON-LD, offers without shipping data, redirect chains, loops and dead targets, orphan products
 * @summary SEO issues, open by default, newest first
 */
export const getAdminV1StoresStoreIdSeoIssues = (
    { storeId }: GetAdminV1StoresStoreIdSeoIssuesPathParameters,
    params?: GetAdminV1StoresStoreIdSeoIssuesParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdSeoIssues200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdSeoIssues200>(
      {url: `/admin/v1/stores/${storeId}/seo/issues`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Mark an issue resolved; the next audit reopens it if it persists
 */
export const postAdminV1StoresStoreIdSeoIssuesIssueIdResolve = (
    { storeId, issueId }: PostAdminV1StoresStoreIdSeoIssuesIssueIdResolvePathParameters,
 options?: SecondParameter<typeof customInstance<SeoIssue>>,) => {
      return customInstance<SeoIssue>(
      {url: `/admin/v1/stores/${storeId}/seo/issues/${issueId}/resolve`, method: 'POST'
    },
      options);
    }
  /**
 * The storefront must serve the key at `{keyLocation}`; template storefronts proxy GET /{key}.txt to the storefront API. The worker then pings IndexNow on every publish, unpublish and rename
 * @summary Create or rotate the store's IndexNow key
 */
export const postAdminV1StoresStoreIdSeoIndexnowKey = (
    { storeId }: PostAdminV1StoresStoreIdSeoIndexnowKeyPathParameters,
 options?: SecondParameter<typeof customInstance<IndexNowKey>>,) => {
      return customInstance<IndexNowKey>(
      {url: `/admin/v1/stores/${storeId}/seo/indexnow-key`, method: 'POST'
    },
      options);
    }
  /**
 * Items are one per sellable variant with a price and an image on a store with a primary domain; `skipped` says why products were dropped (`no_domain`, `no_price`, `no_image`, `no_title`)
 * @summary Merchant feed status: URL, item count and what was left out
 */
export const getAdminV1StoresStoreIdFeeds = (
    { storeId }: GetAdminV1StoresStoreIdFeedsPathParameters,
 options?: SecondParameter<typeof customInstance<FeedStatus>>,) => {
      return customInstance<FeedStatus>(
      {url: `/admin/v1/stores/${storeId}/feeds`, method: 'GET'
    },
      options);
    }
  /**
 * Paste the URL into the channel as a scheduled fetch; rotating invalidates the previous URL
 * @summary Create or rotate a channel's feed URL (google-merchant, meta-catalog, pinterest)
 */
export const postAdminV1StoresStoreIdFeedsChannelRotateToken = (
    { storeId, channel }: PostAdminV1StoresStoreIdFeedsChannelRotateTokenPathParameters,
 options?: SecondParameter<typeof customInstance<FeedToken>>,) => {
      return customInstance<FeedToken>(
      {url: `/admin/v1/stores/${storeId}/feeds/${channel}/rotate-token`, method: 'POST'
    },
      options);
    }
  export type PostAdminV1StoresStoreIdSeoAuditResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdSeoAudit>>>
export type GetAdminV1StoresStoreIdSeoAuditsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdSeoAudits>>>
export type GetAdminV1StoresStoreIdSeoIssuesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdSeoIssues>>>
export type PostAdminV1StoresStoreIdSeoIssuesIssueIdResolveResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdSeoIssuesIssueIdResolve>>>
export type PostAdminV1StoresStoreIdSeoIndexnowKeyResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdSeoIndexnowKey>>>
export type GetAdminV1StoresStoreIdFeedsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdFeeds>>>
export type PostAdminV1StoresStoreIdFeedsChannelRotateTokenResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdFeedsChannelRotateToken>>>
